/**
 * CarePlan API Service
 * Handles all API calls for CarePlan Management
 */

import apiClient from './apiClient';
import {
    CarePlanDTO,
    CreateCarePlanRequest,
    UpdateCarePlanRequest,
} from '../types/careplan.types';

const BASE_PATH = '/v1/careplans';

/**
 * CarePlan Service
 * Provides type-safe API calls for care plan management
 */
class CarePlanService {
    /**
     * Create a new care plan for a resident
     */
    async createCarePlan(request: CreateCarePlanRequest): Promise<CarePlanDTO> {
        const response = await apiClient.post<CarePlanDTO>(BASE_PATH, request);
        return response.data;
    }

    /**
     * Get care plan by ID
     */
    async getCarePlanById(id: string): Promise<CarePlanDTO> {
        const response = await apiClient.get<CarePlanDTO>(`${BASE_PATH}/${id}`);
        return response.data;
    }

    /**
     * Get care plan by resident ID
     * Returns null if no care plan exists for the resident
     */
    async getCarePlanByResidentId(residentId: string): Promise<CarePlanDTO | null> {
        try {
            const response = await apiClient.get<CarePlanDTO>(
                `${BASE_PATH}/resident/${residentId}`
            );
            return response.data;
        } catch (error: any) {
            // If 404, no care plan exists yet - this is expected
            if (error.response?.status === 404) {
                return null;
            }
            // Re-throw other errors
            throw error;
        }
    }

    /**
     * Update existing care plan
     */
    async updateCarePlan(
        id: string,
        request: UpdateCarePlanRequest
    ): Promise<CarePlanDTO> {
        const response = await apiClient.put<CarePlanDTO>(
            `${BASE_PATH}/${id}`,
            request
        );
        return response.data;
    }

    /**
     * Delete care plan (if needed in the future)
     */
    async deleteCarePlan(id: string): Promise<void> {
        await apiClient.delete(`${BASE_PATH}/${id}`);
    }
}

// Export singleton instance
export default new CarePlanService();
